/**
 * ESF y ERI con la presentación del contador.
 *
 * Una sola pantalla parametrizada: los dos estados salen del mismo motor y su
 * única diferencia es qué cifra leen —el balance, el SALDO acumulado; el
 * resultado, el MOVIMIENTO del período— y si van a una o a dos columnas.
 *
 * El detalle por grupo y cuenta es ADITIVO: en «Formato oficial» se dibuja
 * exactamente el mismo estado de siempre, con este mismo código. Subir el
 * detalle cambia al árbol. Lo que se firma no cambia de forma porque hayamos
 * añadido una función de auditoría.
 */

import { Fragment } from "react";
import { Link } from "react-router-dom";
import type { PresentationLine } from "../../api/types";
import { ExpandableStatement } from "../../components/ExpandableStatement";
import { formatMoney } from "../../components/format";
import { StatusBadge } from "../../components/StatusBadge";
import { mayReadJournal, useClosing } from "./closingContext";
import { StatementHead } from "./shared";

interface Props {
  report: "esf" | "eri";
}

export function FormalStatement({ report }: Props) {
  const { package: pkg, session, period } = useClosing();
  const esBalance = report === "esf";
  const lineas = esBalance ? pkg.esf : pkg.eri;
  const campo = esBalance ? "closing_balance" : "period_balance";
  const reportKey = esBalance ? "esf_formal" : "eri_formal";
  const puedeVerApuntes = mayReadJournal(session);

  // Los rótulos van en texto NORMAL y el espaciado lo pone el CSS. Escritos
  // «P A S I V O   Y   P A T R I M O N I O», el navegador colapsa los espacios
  // múltiples, así que la separación entre palabras acababa valiendo lo mismo
  // que la separación entre letras y el rótulo se leía «PASIVOYPATRIMONIO».
  // Además el texto vuelve a ser copiable y legible por lector de pantalla.
  //
  // El PDF no cambia: ahí la cadena espaciada viene del documento oficial, que
  // la trae literal en `sharedStrings.xml`.
  const oficial = esBalance ? (
    <div className="card-columns">
      <StatementColumn
        title="ACTIVO"
        lines={lineas.filter((line) => line.side === "left")}
        field={campo}
      />
      <StatementColumn
        title="PASIVO Y PATRIMONIO"
        lines={lineas.filter((line) => line.side === "right")}
        field={campo}
      />
    </div>
  ) : (
    // La columna de Notas la declara el MAPPING, no la pantalla: el estado
    // tiene que ser reproducible desde su versión, y estaba escrita a mano.
    <StatementColumn
      title=""
      lines={lineas}
      field={campo}
      showNotes={pkg.show_notes?.eri ?? true}
    />
  );

  return (
    <>
      <StatementHead
        title={esBalance ? "Estado de Situación Financiera" : "Estado del Resultado Integral"}
        subtitle={
          esBalance
            ? `al ${pkg.period_to}`
            : `del ${pkg.period_from} al ${pkg.period_to}`
        }
        homologation={pkg.homologation[reportKey]}
      />

      <ExpandableStatement
        reportKey={reportKey}
        period={period}
        official={oficial}
        twoColumn={esBalance}
        field={esBalance ? "presented_closing_amount" : "presented_period_amount"}
        mayReadJournal={puedeVerApuntes}
      />

      {pkg.disclosures.filter((rule) => rule.report === report && rule.gross_amount > 0)
        .length > 0 && (
        <div className="card">
          <h2>Reglas de presentación aplicadas</h2>
          <p className="small muted">
            El importe apartado se declara siempre. Una cifra que desaparece de un
            estado sin dejar rastro es justo lo que esta plataforma evita.
          </p>
          <div className="rows">
            {pkg.disclosures
              .filter((rule) => rule.report === report && rule.gross_amount > 0)
              .map((rule) => (
                <div className="row-item" key={rule.rule_id}>
                  <div className="row-key">
                    <b>
                      <span className="mono">{rule.rule_id}</span>{" "}
                      {rule.text || rule.reason}
                    </b>
                    <span>Efecto neto en el resultado: {formatMoney(rule.net_effect)}</span>
                  </div>
                  <div className="row-val">
                    {formatMoney(rule.gross_amount)}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
}

/** El peso de la fila lo marca su nivel, no su `kind`. */
function claseFila(nivel: PresentationLine["amount_level"]): string | undefined {
  if (nivel === "grand") return "level-1 fila-total";
  // El rótulo de masa TITULA el bloque: va espaciado y sin cifra, como en el
  // documento. Salía con el mismo peso que un subtotal de sección.
  if (nivel === "heading") return "fila-rotulo";
  return nivel === "detail" ? undefined : "level-1";
}

function StatementColumn({
  title,
  lines,
  field,
  showNotes,
}: {
  title: string;
  lines: PresentationLine[];
  field: "closing_balance" | "period_balance";
  showNotes?: boolean;
}) {
  // Cuántas columnas de importe lleva ESTE estado: una más que el escalón más
  // alto que traen sus filas. Sale de los datos y no de una constante local,
  // así que si el mapping gana un nivel la pantalla lo refleja sin tocar nada.
  const pasos = Math.max(1, ...lines.map((line) => line.amount_step + 1));
  // Qué se presenta lo decidió el BACKEND, con la misma medida con la que este
  // estado imprime, y lo mismo ven el PDF y el Excel. Aquí se filtraba por un
  // solo campo mientras el Excel exigía dos y el PDF no filtraba nada: tres
  // respuestas a la misma pregunta contable sobre un documento que se firma.
  const visibles = lines.filter((line) => line.presented);

  // `hoja` en vez de `card`: en «Formato oficial» esta pantalla es la vista
  // previa del documento que se firma, y se presenta como tal —sin borde de
  // tarjeta, sin cabecera azul de tabla y sin línea por fila—. La cabecera
  // recibe el MISMO tratamiento que en el PDF: versalitas sobre un filete. En
  // modo Cuentas la tabla vuelve a comportarse como tabla, que es lo que es.
  /* El rótulo de la columna NO se escribe aquí: lo trae el mapping como fila de
     encabezado —«A C T I V O», «P A S I V O», «P A T R I M O N I O»—, que es de
     donde lo toma el PDF. Escrito además como `<h2>`, la pantalla imprimía
     «ACTIVO» dos veces seguidas: una como título de la tarjeta y otra como
     primera fila de la tabla, mientras el PDF lo imprimía una sola vez. El
     título se conserva como nombre accesible de la tabla, que es lo que un
     lector de pantalla necesita y la vista no debe repetir. */
  return (
    <div className="card hoja">
      <div className="table-wrap documento">
        <table aria-label={title || undefined}>
          <thead>
            <tr>
              <th scope="col">Concepto</th>
              {showNotes && <th scope="col">Nota</th>}
              {Array.from({ length: pasos }, (_, paso) => (
                <th scope="col" className="num" key={paso}>
                  {/* Un solo rótulo para las columnas del escalón: son la MISMA
                      medida a distinto nivel, no dos magnitudes. */}
                  {paso === pasos - 1 ? "Importe" : ""}
                </th>
              ))}
              <th scope="col">Trazabilidad</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((line) => (
              <Fragment key={line.line_id}>
                {/* Filas en blanco para enfrentar «TOTAL ACTIVO» con «TOTAL
                    PASIVO Y PATRIMONIO»: las dos columnas son tablas
                    independientes y cada una terminaba donde se le acababan los
                    renglones, así que los dos totales —cuyo sentido es ser
                    iguales— quedaban a distinta altura. El libro los pone en la
                    misma fila (ESF!D43 y K43) rellenando la columna corta, y
                    cuántas hacen falta lo cuenta el backend. */}
                {Array.from({ length: line.blank_before ?? 0 }, (_, hueco) => (
                  <tr key={`hueco-${hueco}`} className="fila-hueco" aria-hidden="true">
                    <td colSpan={2 + pasos + (showNotes ? 1 : 0)} />
                  </tr>
                ))}
              <tr className={claseFila(line.amount_level)}>
                <td style={{ paddingLeft: 12 + line.depth * 14 }}>
                  {line.label}
                  {/* Solo en pantalla: la descarga oficial con un renglón
                      pendiente está bloqueada, así que nunca llega al papel. */}
                  {line.provisional && (
                    <>
                      {" "}
                      <Link to="/audit/classification" title="Cuentas que el mapping no cubre todavía">
                        <StatusBadge tone="serious">Sin clasificar</StatusBadge>
                      </Link>
                    </>
                  )}
                </td>
                {showNotes && <td className="small muted">{line.note ?? ""}</td>}
                {/* El escalón: el detalle en su columna y el agregado que lo
                    contiene una a la derecha, igual que en el archivo del
                    contador —C11 frente a D10 en la hoja ESF—. Un ESF lleva
                    dos columnas de importe; un ERI, una. */}
                {Array.from({ length: pasos }, (_, paso) => (
                  <td className="num" key={paso}>
                    {line.amount_level !== "heading" && line.amount_step === paso
                      ? formatMoney(line[field])
                      : ""}
                  </td>
                ))}
                <td className="small">
                  {line.amount_level === "detail" && (
                    <Link to={`/closing/annex#${line.line_id}`}>detalle</Link>
                  )}
                </td>
              </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
